import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { Box, Typography } from '@mui/material';
import {
  formatNumber,
  NumberFormatPreset,
  QuickViewReferenceValue,
  QuickViewWidget,
  TinyBarChart,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { isEmpty, isNil } from 'ramda';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { monthsInYearFormatter } from '~/components/CubeChart/CubeChart.common';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { colorFromStatus } from '~/util/conversion';
import { computeChangeRate, getStatus, lastYearDateRange, previousYearDateRange } from '~/util/cube';

const formatChange = formatNumber(NumberFormatPreset.PERCENT_DECIMAL);
const formatValueCompact = formatNumber(NumberFormatPreset.INTEGER_COMPACT);
const formatValue = formatNumber(NumberFormatPreset.INTEGER);

export const DeliveryReliabilityQuickViewWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DeliveryReliabilityQuickViewWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'contract-reliability-quickview' });

  const cube = 'DeliveryDate';

  const filters: Filter[] = [
    {
      member: `${cube}.supplier_id`,
      operator: 'equals',
      values: [entityId],
    },
  ];

  const deliveryDateSeries = useCubeQuery({
    dimensions: [
      `${cube}.date`,
      `${cube}.monthly_purchase_order_items_delivered_on_time`,
      `${cube}.avg_supplier_reliability`,
    ],
    timeDimensions: [
      {
        dimension: `${cube}.date`,
        granularity: 'month',
        dateRange: lastYearDateRange,
      },
    ],
    filters,
  });

  const deliveryDateSeriesPrevious = useCubeQuery({
    dimensions: [`${cube}.date`, `${cube}.monthly_purchase_order_items_delivered_on_time`],
    timeDimensions: [
      {
        dimension: `${cube}.date`,
        granularity: 'month',
        dateRange: previousYearDateRange,
      },
    ],
    filters,
  });

  const segments: any[] | undefined = deliveryDateSeries.resultSet?.rawData()?.map((segment) => ({
    value: Number(segment[`${cube}.monthly_purchase_order_items_delivered_on_time`]),
    reliability: Number(segment[`${cube}.avg_supplier_reliability`]),
    x: segment[`${cube}.date`],
  }));
  const segmentsPrevious: any[] | undefined = deliveryDateSeriesPrevious.resultSet?.rawData()?.map((segment) => ({
    value: Number(segment[`${cube}.monthly_purchase_order_items_delivered_on_time`]),
    x: segment[`${cube}.date`],
  }));

  const isNodesEmpty = isNil(segments) || isEmpty(segments);
  const currentYear: number = segments?.reduce((p, c) => p + c.value, 0) ?? 0;
  const previousYear: number = segmentsPrevious?.reduce((p, c) => p + c.value, 0) ?? 0;
  const changeRate = computeChangeRate(currentYear, previousYear);
  const status = getStatus(changeRate);
  const averageReliability = !isNodesEmpty
    ? segments.reduce((p, c) => p + c.reliability, 0) / segments.length / 100
    : 0;

  return (
    <QuickViewWidget
      ref={widgetRef}
      active={active}
      loading={deliveryDateSeries.isLoading || deliveryDateSeriesPrevious.isLoading}
      title={t('title')}
      subTitle={t('subTitle')}
      value={formatChange(averageReliability)}
      valueTooltip={formatChange(averageReliability)}
      referenceValue={
        <QuickViewReferenceValue
          description={formatValueCompact(previousYear)}
          value={formatChange(changeRate)}
          dotColor={colorFromStatus(status)}
        />
      }
      data-intercom-target="DeliveryReliabilityQuickViewWidget"
    >
      {!isNodesEmpty && currentYear ? (
        <Box
          sx={{
            display: 'flex',
            flex: '1 1 0',
            alignItems: 'flex-end',
          }}
        >
          <Box
            sx={{
              flex: '1 1 0',
              display: 'flex',
              mb: '-5px',
              mr: 3,
            }}
          >
            <Typography component="div" variant="caption" color="textPrimary">
              {t('chartCaption')}
            </Typography>
          </Box>
          <TinyBarChart
            value={segments}
            height={52}
            color="chartPrimary"
            getBarValue={(bar) => bar.value}
            getBarLabel={(bar) =>
              t('barLabel', {
                label: monthsInYearFormatter(bar.x),
                value: formatValue(bar.value),
              })
            }
          />
        </Box>
      ) : (
        <PlaceholderWidget />
      )}
    </QuickViewWidget>
  );
};
